/**
 * 辅助工具函数
 */

import dayjs from 'dayjs';
import { WarningStatus } from '../types';
import { WARNING_STATUS, WARNING_STATUS_COLORS, CITY_LEVELS, DATE_FORMAT, DATETIME_FORMAT } from './constants';
import { formatBeijingTime, TIME_FORMATS } from './timeUtils';

/**
 * 格式化日期
 */
export const formatDate = (date: string | Date, format: string = TIME_FORMATS.DATE): string => {
  return formatBeijingTime(date, format);
};

/**
 * 格式化日期时间
 */
export const formatDateTime = (date: string | Date): string => {
  return formatBeijingTime(date, TIME_FORMATS.DATETIME);
};

/**
 * 获取预警状态标签
 */
export const getWarningStatusLabel = (status: WarningStatus): string => {
  return WARNING_STATUS[status] || '未知';
};

/**
 * 获取预警状态颜色
 */
export const getWarningStatusColor = (status: WarningStatus): string => {
  return WARNING_STATUS_COLORS[status] || '#000000';
};

/**
 * 获取城市等级标签
 */
export const getCityLevelLabel = (level: number): string => {
  return CITY_LEVELS[level as keyof typeof CITY_LEVELS] || '未知等级';
};

/**
 * 格式化数字
 */
export const formatNumber = (num: number, precision: number = 2): string => {
  return Number(num).toFixed(precision);
};

/**
 * 格式化百分比
 */
export const formatPercent = (num: number, precision: number = 1): string => {
  return `${Number(num).toFixed(precision)}%`;
};

/**
 * 格式化文件大小
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * 检查文件类型
 */
export const isValidFileType = (file: File, allowedTypes: string[]): boolean => {
  return allowedTypes.includes(file.type);
};

/**
 * 检查文件大小
 */
export const isValidFileSize = (file: File, maxSize: number): boolean => {
  return file.size <= maxSize;
};

/**
 * 下载文件
 */
export const downloadFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * 防抖函数
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(null, args), wait);
  };
};

/**
 * 节流函数
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func.apply(null, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * 深拷贝对象
 */
export const deepClone = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * 获取对象值的安全方法
 */
export const safeGet = (obj: any, path: string, defaultValue: any = undefined): any => {
  const keys = path.split('.');
  let result = obj;
  
  for (const key of keys) {
    if (result == null || typeof result !== 'object') {
      return defaultValue;
    }
    result = result[key];
  }
  
  return result !== undefined ? result : defaultValue;
};

/**
 * 生成唯一ID
 */
export const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

/**
 * 检查是否为空值
 */
export const isEmpty = (value: any): boolean => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * 获取状态标签样式
 */
export const getStatusTag = (status: WarningStatus) => {
  const config = {
    normal: { color: 'success', text: '正常' },
    warning: { color: 'warning', text: '预警' },
    danger: { color: 'error', text: '危险' },
  };
  
  return config[status] || { color: 'default', text: '未知' };
};